(() => {
  const states = new Map();
  let scrollRaf = 0;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (a, b, value) => {
    const t = clamp((value - a) / Math.max(0.00001, b - a));
    return t * t * (3 - 2 * t);
  };

  const updateState = (state) => {
    const { root, rooms, scenes, groups, progress, nav, hero } = state;
    if (!rooms || !scenes.length || !groups.length) return;

    const viewport = Math.max(1, window.innerHeight);
    const rootRect = root.getBoundingClientRect();
    const roomsRect = rooms.getBoundingClientRect();
    const sceneRects = scenes.map((scene) => scene.getBoundingClientRect());
    const count = Math.min(sceneRects.length, groups.length);
    const denominator = Math.max(1, (count - 1) * viewport);
    const roomProgress = count <= 1 ? 0 : clamp(-roomsRect.top / denominator);
    progress?.style.setProperty('--lmc-room-progress', roomProgress.toFixed(5));

    let activeIndex = 0;
    for (let index = 1; index < count; index += 1) {
      if (sceneRects[index].top <= viewport * 0.44) activeIndex = index;
    }

    groups.forEach((group, index) => {
      const currentTop = sceneRects[index]?.top ?? viewport;
      const nextTop = sceneRects[index + 1]?.top;

      const enter = index === 0
        ? 1 - smooth(viewport * 0.20, viewport * 0.72, currentTop)
        : 1 - smooth(0.40, 0.48, currentTop / viewport);
      const out = nextTop == null ? 1 : smooth(0.52, 0.60, nextTop / viewport);
      const mainOpacity = clamp(Math.min(enter, out));
      const copyIn = smooth(0.26, 0.88, enter);
      const copyOpacity = clamp(copyIn * out);
      const tagExpand = smooth(0.08, 0.68, enter);
      const tagContent = smooth(0.42, 0.92, enter) * out;

      const cardEnter = index === 0
        ? smooth(0.56, 0.96, enter)
        : smooth(0.64, 1.0, enter);
      const cardOut = nextTop == null ? 1 : smooth(0.46, 0.56, nextTop / viewport);
      const cardOpacity = clamp(cardEnter * cardOut);

      group.style.setProperty('--state-enter', enter.toFixed(4));
      group.style.setProperty('--state-opacity', Math.max(mainOpacity, cardOpacity).toFixed(4));
      group.style.setProperty('--state-main-opacity', mainOpacity.toFixed(4));
      group.style.setProperty('--state-copy-opacity', copyOpacity.toFixed(4));
      group.style.setProperty('--tag-expand', tagExpand.toFixed(4));
      group.style.setProperty('--tag-content-opacity', tagContent.toFixed(4));
      group.style.setProperty('--state-card-enter', cardEnter.toFixed(4));
      group.style.setProperty('--state-card-opacity', cardOpacity.toFixed(4));
      group.classList.toggle('is-active', index === activeIndex && mainOpacity > 0.45);
      group.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
    });

    const rootVisible = rootRect.bottom > 0 && rootRect.top < viewport;
    nav?.classList.toggle('is-visible', rootVisible);
    nav?.classList.toggle('is-room-theme', roomsRect.top <= viewport * 0.25 && roomsRect.bottom > viewport * 0.2);

    const mobileLogoVisible = hero?.dataset.mobileLogoVisible || 'false';
    if (nav) nav.dataset.mobileLogoVisible = mobileLogoVisible;
  };

  const updateAll = () => {
    scrollRaf = 0;
    states.forEach(updateState);
  };

  const requestUpdate = () => {
    if (!scrollRaf) scrollRaf = requestAnimationFrame(updateAll);
  };

  const applyBodyState = () => {
    const roots = [...document.querySelectorAll('[data-lmc-home]')];
    const hasActive = roots.length > 0;
    const hideHeader = roots.some((root) => root.dataset.hideThemeHeader === 'true');
    document.body.classList.toggle('lmc-home-page-active', hasActive);
    document.body.classList.toggle('lmc-home-hide-theme-header', hideHeader);
  };

  const initRoot = (root) => {
    if (states.has(root)) return;
    const rooms = root.querySelector('[data-lmc-rooms]');
    if (!rooms) return;
    const state = {
      root,
      rooms,
      scenes: [...rooms.querySelectorAll('[data-lmc-room-scene]')],
      groups: [...rooms.querySelectorAll('[data-lmc-room-state]')],
      progress: rooms.querySelector('[data-lmc-room-progress]'),
      nav: root.querySelector('[data-lmc-home-nav]'),
      hero: root.querySelector('[data-lmc-hero]')
    };
    states.set(root, state);
    updateState(state);
  };

  const destroyRoot = (root) => {
    states.delete(root);
    applyBodyState();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-lmc-home]').forEach(initRoot);
    applyBodyState();
    requestUpdate();
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-lmc-home]').forEach(destroyRoot);
  });
})();
